<!-- ORGANISM: AppBar.vue -->

<script setup lang="ts">
//
// ============================================================================
// IMPORTS
// ============================================================================
//

// Vuetify
import { useDisplay } from "vuetify";

// Components
import AppLogo from "../molecules/AppLogo.vue";

//
// ============================================================================
// PROPS
// ============================================================================
//

interface Props {
  logoSrc?: string | null;
  logoAlt?: string;
  logoSize?: number;
  title?: string;
  subtitle?: string;
  showLogoText?: boolean;
  agreeToTerms: boolean;
}

withDefaults(defineProps<Props>(), {
  logoSrc: null,
  logoAlt: "Laboratory Logo",
  logoSize: 36,
  title: "Laboratory",
  subtitle: "Laboratory Information System",
  showLogoText: true,
});

//
// ============================================================================
// COMPOSABLES
// ============================================================================
//

// Router
const router = useRouter();

// Localization
const { locale, t } = useI18n();
const switchLocalePath = useSwitchLocalePath();

// Breakpoints
const { mobile } = useDisplay();

// Authentication
const { login } = useAuth();

//
// ============================================================================
// CONSTANTS
// ============================================================================
//
const availableLanguages = ["en", "bg", "mk"] as const;

//
// ============================================================================
// STATE
// ============================================================================
//

const languageMenuOpen = ref(false);
const languageExpandOpen = ref(false);
const mobileMenuOpen = ref(false);
const selectedLanguage = ref<"en" | "bg" | "mk">(locale.value as "en" | "bg" | "mk");

const languages = [
  { code: "en" as const, flag: "us" },
  { code: "bg" as const, flag: "bg" },
  { code: "mk" as const, flag: "mk" },
];

//
// ============================================================================
// METHODS
// ============================================================================
//

/**
 * Get flag code for current language
 */
const getCurrentFlag = () => {
  const flagMap: Record<string, string> = {
    en: "us",
    bg: "bg",
    mk: "mk",
  };
  return flagMap[locale.value] || "us";
};

/**
 * Change language
 */
const changeLanguage = (lang: "en" | "bg" | "mk") => {
  selectedLanguage.value = lang;
  const path = switchLocalePath(lang);
  router.push(path);
  languageMenuOpen.value = false;
  mobileMenuOpen.value = false;
};

/**
 * Handle mobile sign in - close menu and login
 */
const handleMobileSignIn = () => {
  mobileMenuOpen.value = false;
  login();
};
</script>

<template>
  <div>
    <v-app-bar app flat sticky class="app-bar border-b px-2 px-sm-4">
      <v-container class="d-flex align-center justify-space-between w-100 px-0">
        <app-logo
          :logo-src="logoSrc"
          :logo-alt="logoAlt"
          :size="logoSize"
          :title="title"
          :subtitle="subtitle"
          :show-text="showLogoText"
        />

        <v-spacer />

        <!-- Desktop: Language Selector & Sign In Button -->
        <div v-if="!mobile" class="d-flex align-center ga-2">
          <!-- Language Selector -->
          <v-menu
            v-model="languageMenuOpen"
            open-on-click
            :close-on-content-click="false"
            location="bottom"
          >
            <template #activator="{ props }">
              <v-btn
                v-tooltip:top="{
                  text: t('pages.login.loginHeader.language_section.title'),
                  openDelay: 500,
                  closeDelay: 200,
                }"
                v-bind="props"
                tabindex="0"
                rounded="lg"
                variant="text"
                class="text-none"
              >
                <CircleFlags
                  :country="getCurrentFlag()"
                  class="mr-2"
                  style="width: 20px; height: 20px; border-radius: 50%; overflow: clip"
                />
                <span class="text-body-1 font-weight-medium">
                  {{ locale.toUpperCase() }}
                </span>
              </v-btn>
            </template>

            <v-list nav density="comfortable" class="px-2">
              <v-list-item
                v-for="lang in languages"
                :key="lang.code"
                :value="lang.code"
                rounded="lg"
                :aria-label="t(`pages.login.loginHeader.language_section.languages.${lang.code}`)"
                @click="changeLanguage(lang.code)"
              >
                <v-list-item-title class="d-flex align-center ga-2">
                  <CircleFlags
                    :country="lang.flag"
                    class="mr-2"
                    style="
                      width: 20px;
                      height: 20px;
                      border-radius: 50%;
                      overflow: clip;
                      border: 1px solid #e0e0e0;
                    "
                  />
                  <span
                    :class="{
                      'font-weight-bold text-primary': selectedLanguage === lang.code,
                    }"
                  >
                    {{ t(`pages.login.loginHeader.language_section.languages.${lang.code}`) }}
                  </span>
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <!-- Sign In Button -->
          <v-btn
            color="primary"
            variant="flat"
            prepend-icon="mdi-login-variant"
            rounded="lg"
            :disabled="!agreeToTerms"
            :text="t('pages.login.loginHeader.signIn')"
            @click="login()"
          />
        </div>

        <!-- Mobile: Menu Dropdown -->
        <v-menu
          v-else
          v-model="mobileMenuOpen"
          location="bottom end"
          :close-on-content-click="false"
        >
          <template #activator="{ props }">
            <v-btn
              v-tooltip="{
                text: t('pages.login.loginHeader.openMenu'),
                location: 'top',
                openDelay: 500,
                closeDelay: 200,
              }"
              v-bind="props"
              icon="mdi-menu"
              variant="text"
              aria-label="Open menu"
            />
          </template>

          <!-- Menu Content -->
          <v-card width="280" rounded="lg" elevation="10" class="py-2">
            <!-- Language Selection Expandable -->
            <v-hover>
              <template #default="{ isHovering, props: hoverProps }">
                <div
                  v-bind="hoverProps"
                  class="text-body-2 px-4 d-flex align-center ga-2 py-2 cursor-pointer"
                  :class="{ 'bg-grey-lighten-3': isHovering }"
                  @click="languageExpandOpen = !languageExpandOpen"
                >
                  <v-icon icon="mdi-web" size="small" />
                  <span>
                    {{ t("pages.login.loginHeader.language_section.title") }}
                  </span>

                  <v-spacer />

                  <v-icon
                    :icon="languageExpandOpen ? 'mdi-chevron-down' : 'mdi-chevron-right'"
                    size="small"
                  />
                </div>
              </template>
            </v-hover>

            <v-expand-transition>
              <v-list v-show="languageExpandOpen" nav density="compact" class="px-4 py-0">
                <v-list-item
                  v-for="lang in languages"
                  :key="lang.code"
                  :value="lang.code"
                  rounded="lg"
                  :class="{
                    'bg-primary text-white': selectedLanguage === lang.code,
                  }"
                  class="mb-1"
                  @click="changeLanguage(lang.code)"
                >
                  <template #prepend>
                    <CircleFlags
                      :country="lang.flag"
                      class="mr-2"
                      style="width: 18px; height: 18px; border-radius: 50%; overflow: clip"
                    />
                  </template>
                  <v-list-item-title class="text-caption">
                    {{ t(`pages.login.loginHeader.language_section.languages.${lang.code}`) }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-expand-transition>

            <v-divider class="my-2" />

            <!-- Sign In Button -->
            <div class="px-4 pt-2">
              <v-btn
                color="primary"
                variant="flat"
                prepend-icon="mdi-login-variant"
                block
                rounded="lg"
                :disabled="!agreeToTerms"
                :text="t('pages.login.loginHeader.signIn')"
                @click="handleMobileSignIn"
              />
            </div>
          </v-card>
        </v-menu>
      </v-container>
    </v-app-bar>
  </div>
</template>

<style scoped>
.app-bar {
  background: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.v-list-item--density-compact.v-list-item--one-line {
  min-height: 0 !important;
}
</style>
